/**
 * Tadhkir — keeps the drawer's custom blocks in step with the cart.
 *
 * The blocks compute their figures in Liquid, so they are only ever as fresh as
 * the last server render. The theme re-renders the drawer when *it* changes the
 * cart, but an app that adds or removes a line itself — a gift-with-purchase app
 * crossing its threshold, say — leaves those figures stale. That is where the
 * subtotal showing the total from before the gift was pulled comes from.
 *
 * So: watch for any cart mutation, however it was made, re-fetch this section,
 * and swap in fresh copies of our blocks only. The theme's own markup is left
 * alone, and the <dialog> is never touched, so the drawer cannot be closed by
 * an update.
 */

const SECTION = window.TDK_CART_SECTION;

/** Blocks to refresh. Order is irrelevant; each is replaced in place. */
const BLOCKS = ['.tdk-goals', '.tdk-promo', '.tdk-total', '.tdk-trust', '.tdk-checkout-wrap'];

/**
 * The promo banner only renders when a discount is actually on the cart, and
 * apps apply theirs asynchronously — so a refresh landing between a cart change
 * and the app re-applying its discount would flash the banner out and back in.
 * Once it has been seen, keep it until the page is left.
 */
const STICKY = ['.tdk-promo'];

let inFlight = false;
let queued = false;
let timer = null;

/**
 * The header's count is only ever written by the theme's own cart events, and
 * it caches itself in sessionStorage: on the next render `cart-icon` restores
 * the cached value over the server's if the two disagree and the cache is less
 * than ten seconds old. So an app that adds or removes a line without firing
 * `cart:lines:update` — a gift app again — leaves a count that survives a page
 * load. Read the real one and write it through the component, which refreshes
 * that cache as a side effect. Without the cache write the stale value would
 * simply come back.
 */
async function syncCount() {
  const root = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';

  const response = await fetch(`${root}.js`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`cart fetch ${response.status}`);

  const count = (await response.json()).item_count ?? 0;

  for (const icon of document.querySelectorAll('cart-icon')) {
    if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(count, false);
  }

  // The drawer prints its own count beside the title, from the same figure.
  const badge = document.querySelector('#cart-drawer .theme-drawer__badge');
  if (badge) {
    badge.textContent = String(count);
    badge.classList.toggle('visually-hidden', count === 0);
  }
}

async function refresh() {
  await syncCount().catch((error) => console.warn('[tdk-cart-sync] count', error));

  if (!SECTION || !document.getElementById('cart-drawer')) return;
  if (inFlight) {
    queued = true;
    return;
  }
  inFlight = true;

  try {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', SECTION);

    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`section fetch ${response.status}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

    for (const selector of BLOCKS) {
      const fresh = doc.querySelector(selector);
      const live = document.querySelector(`#cart-drawer ${selector}`);

      if (fresh && live) {
        live.replaceWith(fresh.cloneNode(true));
      } else if (!fresh && live && !STICKY.includes(selector)) {
        live.remove();
      }
    }
  } catch (error) {
    console.warn('[tdk-cart-sync]', error);
  } finally {
    inFlight = false;
    if (queued) {
      queued = false;
      schedule();
    }
  }
}

/**
 * A cart change made by an app can tear the drawer's <dialog> out of the DOM,
 * and a dialog that leaves the DOM closes. Nothing in the theme puts it back.
 * So remember that it was open across the mutation and reopen it if it went —
 * adding or removing a free gift must not shut the drawer on the shopper.
 */
function guardOpenState() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer || !drawer.hasAttribute('open')) return;

  const reopen = () => {
    const live = document.getElementById('cart-drawer');
    if (live && !live.hasAttribute('open') && typeof live.open === 'function') live.open();
  };

  // The tear-down can land before or after the re-render, so check either side.
  for (const delay of [150, 600, 1200]) setTimeout(reopen, delay);
}

/** Cart mutations arrive in bursts — one refresh per burst is enough. */
function schedule() {
  guardOpenState();
  clearTimeout(timer);
  timer = setTimeout(refresh, 350);
}

// The theme's own events, plus the common names apps dispatch.
for (const name of [
  'cart:update',
  'cart:updated',
  'cart:change',
  'cart:refresh',
  'cart:lines:update',
  'cart:item:added',
]) {
  document.addEventListener(name, schedule);
}

// An app that posts to the cart without announcing it is the case that started
// this, so watch the endpoint rather than trusting anyone to fire an event.
const nativeFetch = window.fetch;
window.fetch = async function tdkFetch(...args) {
  const response = await nativeFetch.apply(this, args);

  try {
    const target = args[0];
    const href = typeof target === 'string' ? target : (target?.url ?? '');
    if (/\/cart\/(add|change|update|clear)(\.js)?/.test(href)) schedule();
  } catch {
    // Never let bookkeeping break someone else's request.
  }

  return response;
};

// XHR too — older apps still use it.
const nativeOpen = XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open = function tdkOpen(method, url, ...rest) {
  if (typeof url === 'string' && /\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
    this.addEventListener('load', schedule, { once: true });
  }
  return nativeOpen.call(this, method, url, ...rest);
};

// A count left wrong by an earlier visit is cached in sessionStorage and gets
// restored over the server's figure on load, so correct it once on arrival too.
// After `cart-icon` has connected, or the component would not be there to write.
requestAnimationFrame(() => {
  syncCount().catch((error) => console.warn('[tdk-cart-sync] count', error));
});
